
const OperatingModelSection = () => {
  const phases = [
    {
      number: '01',
      title: 'Discovery & Assessment',
      description: 'Comprehensive analysis of your current operations, challenges, and strategic objectives',
      icon: 'ri-search-eye-line',
    },
    {
      number: '02',
      title: 'Strategy Development',
      description: 'Tailored strategic roadmap aligned with your business goals and market dynamics',
      icon: 'ri-lightbulb-line',
    },
    {
      number: '03',
      title: 'Implementation',
      description: 'Execution through vetted partners with proven expertise in your industry',
      icon: 'ri-settings-3-line',
    },
    {
      number: '04',
      title: 'Optimization',
      description: 'Continuous monitoring and refinement to ensure sustained operational excellence',
      icon: 'ri-line-chart-line',
    },
  ];

  const models = [
    {
      title: 'Strategic Advisory',
      subtitle: 'MACGOC Leadership',
      description: 'Expert guidance from our executive team across all five strategic divisions',
      features: ['C-Suite Engagement', 'Industry Expertise', 'Strategic Planning', 'Risk Assessment'],
      icon: 'ri-compass-3-line',
    },
    {
      title: 'Execution Partners',
      subtitle: 'Vetted Network',
      description: 'Implementation through carefully selected specialists with proven track records',
      features: ['Technical Specialists', 'Industry Veterans', 'Certified Professionals', 'Local Expertise'],
      icon: 'ri-team-line',
    },
    {
      title: 'Continuous Support',
      subtitle: 'Long-Term Partnership',
      description: 'Ongoing operational excellence through sustained engagement and monitoring',
      features: ['Performance Tracking', 'Regular Reviews', 'Adaptive Strategy', '24/7 Support'],
      icon: 'ri-shield-check-line',
    },
  ];

  return (
    <section className="bg-black py-28 border-t border-gray-900">
      <div className="max-w-[1600px] mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p 
            className="text-sm uppercase tracking-[0.3em] mb-6"
            style={{
              background: 'linear-gradient(90deg, #7A6A5A 0%, #C4B8A8 50%, #7A6A5A 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Our Approach
          </p>
          <h2 className="text-4xl lg:text-5xl font-light text-white mb-6">
            How MACGOC Delivers
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            A proven methodology combining strategic advisory excellence with execution partnerships to deliver measurable results
          </p>
        </div>

        {/* Process Flow Diagram */}
        <div className="mb-24">
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-16 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-copper/20 via-copper/40 to-copper/20"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {phases.map((phase, index) => (
                <div key={index} className="relative text-center">
                  {/* Circle with Number */}
                  <div className="relative z-10 mx-auto w-32 h-32 rounded-full border-2 border-copper/30 bg-charcoal-dark flex flex-col items-center justify-center mb-6 group hover:border-copper transition-colors duration-300">
                    <div className="w-10 h-10 flex items-center justify-center mb-1">
                      <i className={`${phase.icon} text-3xl text-copper`}></i>
                    </div>
                    <span 
                      className="text-xs font-semibold tracking-wider"
                      style={{
                        background: 'linear-gradient(90deg, #A89078 0%, #D8CCC0 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      PHASE {phase.number}
                    </span>
                  </div>
                  
                  {/* Arrow for desktop */}
                  {index < phases.length - 1 && (
                    <div className="hidden lg:block absolute top-16 -right-4 w-8 h-8 flex items-center justify-center z-20">
                      <i className="ri-arrow-right-line text-copper text-xl"></i>
                    </div>
                  )}
                  
                  <h3 className="text-lg font-semibold text-white mb-3">{phase.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">{phase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Advisory + Execution Model */}
        <div className="bg-charcoal-dark rounded-3xl border border-gray-800 p-12 lg:p-16">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-semibold text-white mb-4">Advisory + Execution Model</h3>
            <p className="text-gray-400 max-w-2xl mx-auto">
              MACGOC combines strategic advisory leadership with a network of vetted execution partners to deliver comprehensive solutions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {models.map((model, index) => (
              <div
                key={index}
                className="relative bg-charcoal rounded-2xl border border-gray-800 p-8 hover:border-copper/50 transition-all duration-300 group"
              >
                {/* Icon */}
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                  style={{
                    background: 'linear-gradient(135deg, rgba(122, 106, 90, 0.3) 0%, rgba(168, 144, 120, 0.2) 100%)',
                    border: '1px solid rgba(168, 144, 120, 0.3)',
                  }}
                >
                  <i className={`${model.icon} text-3xl text-copper`}></i>
                </div>

                {/* Subtitle */}
                <p 
                  className="text-xs uppercase tracking-wider mb-2"
                  style={{
                    background: 'linear-gradient(90deg, #A89078 0%, #D8CCC0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {model.subtitle}
                </p>

                {/* Title */}
                <h4 className="text-2xl font-semibold text-white mb-4">{model.title}</h4>

                {/* Description */}
                <p className="text-gray-400 text-sm leading-relaxed mb-6">{model.description}</p>

                {/* Features */}
                <ul className="space-y-3">
                  {model.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-gray-300">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-check-line text-copper"></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Connector Arrow (between cards) */}
                {index < models.length - 1 && (
                  <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-charcoal-dark border border-gray-800 rounded-full items-center justify-center z-10">
                    <i className="ri-arrow-right-line text-copper"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom Flow Indicator */}
          <div className="mt-12 flex items-center justify-center gap-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-copper/50"></div>
            <div className="px-6 py-3 border border-copper/30 rounded-full">
              <span className="text-sm text-copper font-medium">Measurable Results & ROI</span>
            </div>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-copper/50"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OperatingModelSection;
